import { useGetRoutes } from "@features/mx/validations/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { PAGE_SIZE } from "@shared/utils/constants";
import { X } from "lucide-react";
import { useState } from "react";

export const AssignRoutesModal = ({
  onClose,
  onAssignReader,
  selectedRows,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const { data: routeData } = useGetRoutes(
    {
      page: currentPage,
      limit: PAGE_SIZE,
      search_data: searchValue,
    },
    selectedRows
  );

  const columns = [
    {
      key: "routeName",
      header: "Route Name",
      type: "custom",
      render: (value) => (
        <div className="font-medium text-blue-600">{value || "N/A"}</div>
      ),
    },
    {
      key: "meterCount",
      header: "Meters",
      type: "text",
    },
    {
      key: "consumerCount",
      header: "Consumers",
      type: "text",
    },
    {
      key: "premiseCount",
      header: "Premises",
      type: "text",
    },
    {
      key: "meterReaderCount",
      header: "Assigned To",
    },
    {
      key: "assignDate",
      header: "Assigned Date",
    },
    {
      key: "dueDate",
      header: "Due Date",
    },
    {
      key: "status",
      header: "Status",
    },
  ];

  const handleAssignRoute = (route) => {
    onClose(); // Close the Assign Routes modal
    setTimeout(() => {
      onAssignReader(route); // Open the Assign Reader modal with the selected route
    }, 200);
  };

  const actions = [
    {
      label: "Assign",
      icon: "Users",
      onClick: (row) => handleAssignRoute(row),
      variant: "ghost",
    },
  ];

  const pagination = {
    currentPage,
    totalPages: Math.ceil((routeData?.count || 0) / PAGE_SIZE),
    totalItems: routeData?.count || 0,
    itemsPerPage: PAGE_SIZE,
    startItem: (currentPage - 1) * PAGE_SIZE + 1,
    endItem: Math.min(currentPage * PAGE_SIZE, routeData?.count || 0),
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg w-[70%] max-w-7xl max-h-[90vh] overflow-hidden shadow-lg">
        {/* Header */}
        <div className="flex justify-between items-start p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Assign Routes
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Select a route to assign to the selected meter readings for
              revisit.
            </p>
            <p className="text-xs text-blue-600 mt-1">
              {routeData?.count || 0} dispatch routes found
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Table Container */}
        <div className="p-6 overflow-auto max-h-[calc(90vh-120px)]">
          <GenericTable
            data={routeData?.results || []}
            columns={columns}
            rowKey="id"
            actions={actions}
            actionsType="icons"
            pagination={pagination}
            onPageChange={setCurrentPage}
            emptyMessage="No routes found"
            searchPlaceholder="Search by route name..."
            onSearch={(searchTerm) => setSearchValue(searchTerm)}
          />
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
